import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  SegmentVideoPlayerHandle,
  SegmentVideoPlayerPlaybackState,
} from './segment-video-player';

export const TIMELINE_ZOOM_SCALES = [1, 2, 3, 4] as const;
export type TimelineZoomScale = (typeof TIMELINE_ZOOM_SCALES)[number];

type EditViewPlayerContextValue = {
  playerRef: React.MutableRefObject<SegmentVideoPlayerHandle | null>;
  outputTime: number;
  isPlaying: boolean;
  zoomScale: TimelineZoomScale;
  setZoomScale: (nextScale: TimelineZoomScale) => void;
  play: () => void;
  pause: () => void;
  seek: (outputTime: number) => void;
  previewSegmentTime: (segmentId: string, sourceTimeSec: number) => void;
  togglePlay: () => void;
  onPlaybackStateChange: (state: SegmentVideoPlayerPlaybackState) => void;
};

const EditViewPlayerContext = createContext<EditViewPlayerContextValue | null>(
  null,
);

type EditViewPlayerProviderProps = {
  children: React.ReactNode;
};

export const EditViewPlayerProvider = ({
  children,
}: EditViewPlayerProviderProps) => {
  const playerRef = useRef<SegmentVideoPlayerHandle | null>(null);
  const [outputTime, setOutputTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [zoomScale, setZoomScale] = useState<TimelineZoomScale>(1);

  const play = useCallback(() => {
    playerRef.current?.play();
  }, []);

  const pause = useCallback(() => {
    playerRef.current?.pause();
  }, []);

  const seek = useCallback((nextOutputTime: number) => {
    playerRef.current?.seek(nextOutputTime);
  }, []);

  const previewSegmentTime = useCallback(
    (segmentId: string, sourceTimeSec: number) => {
      playerRef.current?.previewSegmentTime(segmentId, sourceTimeSec);
    },
    [],
  );

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      playerRef.current?.pause();
      return;
    }
    playerRef.current?.play();
  }, [isPlaying]);

  const onPlaybackStateChange = useCallback(
    (state: SegmentVideoPlayerPlaybackState) => {
      setOutputTime(state.outputTime);
      setIsPlaying(state.isPlaying);
    },
    [],
  );

  const value = useMemo<EditViewPlayerContextValue>(
    () => ({
      playerRef,
      outputTime,
      isPlaying,
      zoomScale,
      setZoomScale,
      play,
      pause,
      seek,
      previewSegmentTime,
      togglePlay,
      onPlaybackStateChange,
    }),
    [
      isPlaying,
      onPlaybackStateChange,
      outputTime,
      pause,
      play,
      previewSegmentTime,
      seek,
      togglePlay,
      zoomScale,
    ],
  );

  return (
    <EditViewPlayerContext.Provider value={value}>
      {children}
    </EditViewPlayerContext.Provider>
  );
};

export const useEditViewPlayer = () => {
  const context = useContext(EditViewPlayerContext);
  if (!context) {
    throw new Error(
      'useEditViewPlayer must be used within an EditViewPlayerProvider',
    );
  }
  return context;
};
